import { graphHooks } from '@tunasong/graph-lib/react'
import { Check, Edit } from '@tunasong/icons'
import { isAIGenerated, isScaffold, SystemTags } from '@tunasong/models'
import type { RenderMenuItemProps } from '@tunasong/plugin-lib'
import type { Entity } from '@tunasong/schemas'
import { isEntity, isPersisted } from '@tunasong/schemas'
import { ListItemIcon, ListItemText, MenuItem, useAlert } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEntityBackedElement } from '../hooks/persisted-element.js'

/**
 * Promote a sketch to the work, or send it back.
 *
 * Promotion is the point of the two-marker model rather than a loophole in it. The strongest
 * research finding is authorship — "it's not my song anymore" — and a status the user can never
 * clear is the product asserting that judgement permanently. "Scaffolding is ephemeral" is a
 * strong default, not a claim about the material: excluded *until you say otherwise* is
 * defensible, excluded *forever* overrules the author.
 *
 * Keep moves status only. `sys:ai-generated` stays, and surfaces in the Properties popover, so
 * anyone asking where the media came from still gets a straight answer.
 *
 * This lives in the block menu rather than the rail on purpose: promotion is a decision, not a
 * reflex, and a click away from Discard it would get hit by accident on exactly the material a
 * musician is most ambivalent about.
 */
export const ScaffoldStatusAction: FC<RenderMenuItemProps> = props => {
  const { element, onClose } = props

  const { alert } = useAlert()
  const updateEntity = graphHooks.useEntityUpdate()
  const { entity } = useEntityBackedElement(element as Entity)

  // Only generated media has a status to move. A user's own upload can never become a sketch.
  if (!(isEntity(entity) && isPersisted(entity) && isAIGenerated(entity))) {
    return null
  }

  const isSketch = isScaffold(entity)
  const withoutStatus = (entity.tags ?? []).filter(t => t !== SystemTags.Scaffold)

  const handleClick = () => {
    if (isSketch) {
      updateEntity(entity.id, { tags: withoutStatus })
      // The one-word menu item can't explain what just changed; this does.
      alert({ severity: 'success', message: "Kept. It'll print and export like anything else now." })
    } else {
      updateEntity(entity.id, { tags: [...withoutStatus, SystemTags.Scaffold] })
    }
    onClose?.()
  }

  return (
    <MenuItem onClick={handleClick}>
      <ListItemIcon>{isSketch ? <Check /> : <Edit />}</ListItemIcon>
      <ListItemText>{isSketch ? 'Keep' : 'Return to sketch'}</ListItemText>
    </MenuItem>
  )
}

export default ScaffoldStatusAction
